import type React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { GripVertical, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IClaimFormFieldConfig } from '@/@types/claims';
import type { EditorAction } from './editorReducer';

// LOCAL_TYPE_JUSTIFIED: editor-internal props — tightly scoped to this feature, not reused elsewhere
interface IFieldEditorProps {
  field: IClaimFormFieldConfig;
  sectionKey: string;
  dispatch: React.Dispatch<EditorAction>;
}

export function FieldEditor({ field, sectionKey, dispatch }: IFieldEditorProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.fieldKey,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex flex-col gap-2 py-3 transition-shadow',
        !field.visible && 'opacity-50',
        isDragging && 'shadow-lg opacity-80 bg-background z-50 rounded-md'
      )}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none p-0.5 rounded text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder field"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Input
          value={field.label}
          onChange={e => dispatch({ type: 'UPDATE_FIELD_LABEL', sectionKey, fieldKey: field.fieldKey, label: e.target.value })}
          className="flex-1 h-8 text-sm"
        />
        <div className="flex items-center gap-1.5 shrink-0">
          <Label className="text-xs text-muted-foreground">Visible</Label>
          <Switch
            checked={field.visible}
            onCheckedChange={() => dispatch({ type: 'TOGGLE_FIELD_VISIBLE', sectionKey, fieldKey: field.fieldKey })}
          />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Label className="text-xs text-muted-foreground">Required</Label>
          <Switch
            checked={field.required}
            onCheckedChange={() => dispatch({ type: 'TOGGLE_FIELD_REQUIRED', sectionKey, fieldKey: field.fieldKey })}
          />
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
          onClick={() => dispatch({ type: 'REMOVE_FIELD', sectionKey, fieldKey: field.fieldKey })}
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
      {field.options !== undefined && (
        <div className="ml-7 flex items-center gap-2">
          <Label className="text-xs text-muted-foreground shrink-0">Options (comma-separated)</Label>
          <Input
            value={field.options.join(', ')}
            onChange={e =>
              dispatch({
                type: 'UPDATE_FIELD_OPTIONS',
                sectionKey,
                fieldKey: field.fieldKey,
                options: e.target.value.split(',').map(o => o.trim()).filter(Boolean),
              })
            }
            className="h-7 text-xs"
          />
        </div>
      )}
    </div>
  );
}
